import { Sparkle, X } from '@phosphor-icons/react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import introBg from '../../../assets/walkthrough-intro-bg.jpg';
import introShot from '../../../assets/walkthrough-intro-shot.png';
import { MODAL_SCRIM_CLASS } from '../../ui/modalScrim';

// Announcement modal shown BEFORE the inline-comments walkthrough (Figma
// 4619:116823 text panel + 4619:116835 visual panel — a 693x398 split).
// "Try it out" hands off to the walkthrough; "Maybe later" / × dismiss
// the whole intro flow. The gradient artwork ships as the design's own
// export, so its blur/mesh is pixel-identical by construction; per-node
// fractional values (10.09px labels, 0.561px border, 16.34px offsets)
// are reproduced verbatim — they're scaled instances in the file.
//
// Built on Radix's Dialog primitives (not the pre-styled DialogContent
// wrapper — this needs its own pixel-exact layout) for real focus-trap +
// Escape-to-dismiss + aria-modal, instead of a hand-rolled <div> that a
// keyboard user could tab straight through into the "inert" background.
// Outside clicks are intentionally inert (product decision, not an
// oversight) — only the three buttons or Escape do anything.

export function CommentIntroModal({
  onTryIt,
  onDismiss,
}: {
  onTryIt: () => void;
  onDismiss: () => void;
}) {
  return (
    <DialogPrimitive.Root
      open
      modal
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={MODAL_SCRIM_CLASS} />
        <DialogPrimitive.Content
          aria-modal="true"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed inset-0 z-50 flex items-center justify-center overscroll-none pl-[60px] outline-none"
        >
          <div className="flex h-[398px] w-[693px] overflow-hidden rounded-xl bg-beige-100">
            {/* Text panel (4619:116823): 48/40 padding, 296px column, content
                top / buttons bottom. */}
            <div className="flex h-full w-[347px] shrink-0 flex-col items-center justify-center px-12 py-10">
              <div className="flex min-h-0 w-[296px] flex-1 flex-col items-start justify-between">
                <div className="flex w-full flex-col items-start gap-1.5">
                  <div className="flex items-center gap-1 rounded-3xl bg-[#6c09f7] px-1.5 py-0.5">
                    <Sparkle size={12} weight="fill" className="text-white" />
                    <p className="text-[12px] leading-4 text-white">New</p>
                  </div>
                  <div className="flex w-full flex-col gap-2">
                    <DialogPrimitive.Title asChild>
                      <p className="text-[24px] font-medium leading-8 text-black">
                        Comment right on the line you&rsquo;re reading
                      </p>
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description asChild>
                      <p className="text-[14px] font-medium leading-5 text-muted-foreground">
                        Highlight any line, add your note in context, and every comment opens a live
                        thread.
                      </p>
                    </DialogPrimitive.Description>
                  </div>
                </div>
                <div className="flex w-full items-start gap-3">
                  <button
                    type="button"
                    onClick={onDismiss}
                    className="flex h-9 min-w-px flex-1 items-center justify-center rounded-full border-[0.561px] border-black text-[10.09px] font-medium leading-[15.695px] text-black"
                  >
                    Maybe later
                  </button>
                  <button
                    type="button"
                    onClick={onTryIt}
                    className="flex h-9 min-w-px flex-1 items-center justify-center rounded-full bg-primary text-[10.09px] font-medium leading-[15.695px] text-primary-foreground"
                  >
                    Try it out
                  </button>
                </div>
              </div>
            </div>

            {/* Visual panel (4619:116835): the design's exported gradient as the
                ground, the app screenshot offset past center so it crops off the
                bottom-right — exactly as framed in the file. */}
            <div className="relative h-full flex-1 overflow-hidden">
              <img src={introBg} alt="" className="absolute inset-0 size-full object-cover" />
              <div className="absolute left-[calc(50%+46.5px)] top-[calc(50%+63.5px)] -translate-x-1/2 -translate-y-1/2 rounded-[11.211px] border border-white/40 p-2">
                <img
                  src={introShot}
                  alt=""
                  className="h-[338px] w-[364px] max-w-none rounded-lg object-cover"
                />
              </div>
              <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss announcement"
                className="absolute right-[16.34px] top-4 flex size-[24.66px] items-center justify-center rounded-full bg-background-primary-light"
              >
                <X size={11.2} className="text-black" />
              </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
